import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Spinner } from '@lex/ui';
import { apiClient } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ProfileSecurity } from '../components/cabinet/ProfileSecurity';

export function CabinetPage() {
  const { user } = useAuth();
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState<'no_result' | 'wrong_plan' | 'duplicate' | 'other'>('no_result');
  const [refundComment, setRefundComment] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const conversations = useQuery({
    queryKey: ['cabinet', 'conversations'],
    queryFn: () => apiClient.chat.listConversations({ page: 1 }),
  });
  const requests = useQuery({
    queryKey: ['cabinet', 'requests'],
    queryFn: () => apiClient.auth.myRequests(),
  });
  const subscription = useQuery({
    queryKey: ['cabinet', 'subscription'],
    queryFn: () => apiClient.chat.subscription(),
  });
  const refunds = useQuery({
    queryKey: ['cabinet', 'refunds'],
    queryFn: () => apiClient.chat.refunds(),
  });
  const payments = useQuery({
    queryKey: ['cabinet', 'payments'],
    queryFn: () => apiClient.chat.payments(),
  });
  const documents = useQuery({
    queryKey: ['cabinet', 'documents', conversations.data?.data.map((item) => item.id).join(',')],
    enabled: Boolean(conversations.data),
    queryFn: async () => {
      const list = conversations.data?.data ?? [];
      const groups = await Promise.all(
        list.map(async (conversation) => {
          const files = await apiClient.chat.listAttachments(conversation.id).catch(() => []);
          return files.map((file) => ({ ...file, conversationId: conversation.id }));
        })
      );
      return groups.flat();
    },
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-ink md:text-3xl">Личный кабинет</h1>
        <p className="mt-1 text-sm text-muted">
          Способ входа:{' '}
          <span className="font-medium text-ink">
            {user?.loginMethod === 'phone'
              ? 'телефон'
              : user?.loginMethod === 'email'
                ? 'почта'
                : 'Telegram'}
          </span>
        </p>
      </div>


      <ProfileSecurity />

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Мои дела</h2>
          <Link to="/cases" className="text-sm font-semibold text-accent">
            Все дела
          </Link>
        </div>
        <Card className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted">
            Этапы работы, сроки, документы и черновики по вашим обращениям.
          </p>
          <Link
            to="/cases"
            className="shrink-0 rounded-xl border border-line bg-white px-4 py-2 text-sm font-semibold text-ink"
          >
            Открыть
          </Link>
        </Card>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Документы</h2>
          <span className="text-sm text-muted">{documents.data?.length ?? 0}</span>
        </div>
        <Card className="space-y-2">
          {(documents.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted">Файлов пока нет.</p>
          ) : (
            documents.data?.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 rounded-xl border border-line px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate text-ink">{file.name}</span>
                <a
                  href={file.url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-accent"
                >
                  Открыть
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(`Удалить файл «${file.name}»?`)) return;
                    void apiClient.chat
                      .deleteAttachment(file.conversationId, file.id)
                      .then(() => documents.refetch());
                  }}
                  className="text-xs font-semibold text-danger"
                >
                  Удалить
                </button>
              </div>
            ))
          )}
        </Card>
      </section>

      <section id="subscription" className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Подписка</h2>
          <Link to="/#tariffs" className="text-sm font-semibold text-accent">
            Тарифы
          </Link>
        </div>
        {subscription.isLoading ? (
          <Spinner />
        ) : subscription.data ? (
          <Card className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-lg font-semibold text-ink">{subscription.data.planName}</div>
                <div className="text-xs text-muted">
                  {new Date(subscription.data.startedAt).toLocaleDateString('ru-RU')}
                  {subscription.data.expiresAt
                    ? ` — ${new Date(subscription.data.expiresAt).toLocaleDateString('ru-RU')}`
                    : ' · бессрочно'}
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-ink">
                  {subscription.data.price} {subscription.data.currency}
                </div>
                <div className="text-xs text-success">Активна</div>
              </div>
            </div>
            <label className="flex items-center justify-between gap-3 rounded-xl bg-soft px-3.5 py-2.5 text-sm text-ink">
              <span>Автопродление</span>
              <input
                type="checkbox"
                checked={subscription.data.autoRenew}
                onChange={(event) => {
                  void apiClient.chat
                    .setAutoRenew(event.target.checked)
                    .then(() => subscription.refetch());
                }}
              />
            </label>
            <div className="flex items-center justify-between text-[13px] text-muted">
              <span>Способ оплаты</span>
              <span className="font-medium text-ink">
                {payments.data?.find((item) => item.status === 'succeeded')?.cardMask ??
                  'карта не привязана'}
              </span>
            </div>
            {!refundOpen ? (
              <button
                type="button"
                onClick={() => {
                  setRefundAmount(String(subscription.data?.price ?? ''));
                  setRefundOpen(true);
                }}
                className="text-sm font-semibold text-accent"
              >
                Запросить возврат
              </button>
            ) : (
              <div className="space-y-3 rounded-2xl border border-line p-4">
                <label className="block space-y-1.5 text-sm">
                  <span className="font-medium text-ink">Причина</span>
                  <select
                    value={refundReason}
                    onChange={(event) => setRefundReason(event.target.value as typeof refundReason)}
                    className="w-full rounded-lg border border-line px-3 py-2 text-ink"
                  >
                    <option value="no_result">Не получил результата</option>
                    <option value="wrong_plan">Ошибся с тарифом</option>
                    <option value="duplicate">Двойное списание</option>
                    <option value="other">Другая причина</option>
                  </select>
                </label>
                <label className="block space-y-1.5 text-sm">
                  <span className="font-medium text-ink">Сумма</span>
                  <input
                    value={refundAmount}
                    onChange={(event) => setRefundAmount(event.target.value)}
                    inputMode="numeric"
                    className="w-full rounded-lg border border-line px-3 py-2 text-ink"
                  />
                </label>
                <label className="block space-y-1.5 text-sm">
                  <span className="font-medium text-ink">Комментарий</span>
                  <textarea
                    value={refundComment}
                    onChange={(event) => setRefundComment(event.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-line px-3 py-2 text-ink"
                  />
                </label>
                {refundError ? <p className="text-xs text-danger">{refundError}</p> : null}
                <div className="flex gap-2">
                  <Button
                    loading={refundBusy}
                    onClick={() => {
                      const amount = Number(refundAmount);
                      if (!Number.isFinite(amount) || amount <= 0) {
                        setRefundError('Укажите сумму больше нуля');
                        return;
                      }
                      setRefundBusy(true);
                      setRefundError(null);
                      void apiClient.chat
                        .requestRefund({
                          amount,
                          reason: refundReason,
                          ...(refundComment.trim() ? { comment: refundComment.trim() } : {}),
                        })
                        .then(() => {
                          setRefundOpen(false);
                          setRefundComment('');
                          void refunds.refetch();
                        })
                        .catch((err: unknown) =>
                          setRefundError(err instanceof Error ? err.message : 'Не удалось отправить заявку')
                        )
                        .finally(() => setRefundBusy(false));
                    }}
                  >
                    Отправить заявку
                  </Button>
                  <Button variant="secondary" onClick={() => setRefundOpen(false)}>
                    Отмена
                  </Button>
                </div>
              </div>
            )}
            {(refunds.data?.length ?? 0) > 0 ? (
              <div className="space-y-2 border-t border-line pt-3 text-sm">
                {refunds.data?.map((refund) => (
                  <div
                    key={refund.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-soft px-3 py-2"
                  >
                    <div>
                      <div className="text-[13px] font-medium text-ink">
                        Возврат {refund.amount} {refund.currency}
                      </div>
                      <div className="text-[11.5px] text-muted">
                        {new Date(refund.createdAt).toLocaleDateString('ru-RU')} ·{' '}
                        {refund.reason === 'no_result'
                          ? 'не получил результата'
                          : refund.reason === 'wrong_plan'
                            ? 'ошибся с тарифом'
                            : refund.reason === 'duplicate'
                              ? 'двойное списание'
                              : 'другая причина'}
                      </div>
                    </div>
                    <span
                      className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${
                        refund.status === 'approved'
                          ? 'bg-success-soft text-success'
                          : refund.status === 'rejected'
                            ? 'bg-danger-soft text-danger'
                            : 'bg-white text-muted'
                      }`}
                    >
                      {refund.status === 'new'
                        ? 'На рассмотрении'
                        : refund.status === 'approved'
                          ? 'Одобрен'
                          : 'Не одобрен'}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-muted">
              Подписка не оформлена. Выберите тариф, чтобы получить доступ ко всем возможностям.
            </p>
          </Card>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Мои диалоги</h2>
          <Link to="/chat" className="text-sm font-semibold text-accent">
            Открыть чат
          </Link>
        </div>
        {conversations.isLoading ? (
          <Spinner />
        ) : (conversations.data?.data.length ?? 0) === 0 ? (
          <Card>Пока нет диалогов</Card>
        ) : (
          <div className="space-y-3">
            {conversations.data?.data.map((conversation) => (
              <Card key={conversation.id} className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-ink">{conversation.subject ?? conversation.topic}</div>
                  <div className="text-xs text-muted">{conversation.status}</div>
                </div>
                <Link to="/chat" className="text-sm font-semibold text-accent">
                  Открыть
                </Link>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-ink">Мои обращения</h2>
        {requests.isLoading ? (
          <Spinner />
        ) : (requests.data?.length ?? 0) === 0 ? (
          <Card>Обращений пока нет</Card>
        ) : (
          <div className="space-y-3">
            {requests.data?.map((request) => (
              <Card key={request.id}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-ink">{request.question}</span>
                  <span className="text-xs text-muted">{request.status}</span>
                </div>
                <div className="mt-1 text-xs text-muted">
                  {new Date(request.createdAt).toLocaleString('ru-RU')}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

